import { ModeCalculTarification, TarifDto } from 'api/funnel';
import { isExpressionMode, isMontantHTMode, isTauxMode } from './funnelEnums';

/**
 * Formatage partagé par tous les onglets du détail tunnel.
 * L'ancien back-office recopiait ces conversions dans chaque template, avec des
 * arrondis et des séparateurs différents d'un écran à l'autre.
 */

const EUR = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const PERCENT = new Intl.NumberFormat('fr-FR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 4,
});

export const DASH = '—';

export const formatAmount = (value?: number | null): string =>
  value === null || value === undefined ? DASH : EUR.format(value);

export const formatRate = (value?: number | null): string =>
  value === null || value === undefined ? DASH : `${PERCENT.format(value)} %`;

export const formatDate = (value?: Date | string | null): string =>
  value ? new Date(value).toLocaleDateString('fr-FR') : DASH;

export const formatInteger = (value?: number | null): string =>
  value === null || value === undefined ? DASH : String(value);

/**
 * Rend une tarification selon son mode de calcul : un taux ne doit jamais s'afficher
 * en euros, ni une expression en montant.
 */
export const formatTarif = (tarif?: TarifDto | null): string => {
  if (!tarif) return DASH;

  if (isTauxMode(tarif.modeCalcul)) return formatRate(tarif.taux);
  if (isExpressionMode(tarif.modeCalcul)) return tarif.expression || DASH;
  if (isMontantHTMode(tarif.modeCalcul)) {
    return tarif.montantFixeHT === null || tarif.montantFixeHT === undefined
      ? DASH
      : `${formatAmount(tarif.montantFixeHT)} HT`;
  }
  return formatAmount(tarif.montantFixe);
};

/** Valeur numérique portée par une tarification, indépendamment du mode. */
export const tarifValue = (tarif?: TarifDto | null): number | null => {
  if (!tarif) return null;
  if (isTauxMode(tarif.modeCalcul)) return tarif.taux ?? null;
  if (isMontantHTMode(tarif.modeCalcul)) return tarif.montantFixeHT ?? null;
  if (isExpressionMode(tarif.modeCalcul)) return null;
  return tarif.montantFixe ?? null;
};

/**
 * Construit la partie montant d'une commande de tarification : seul le champ
 * correspondant au mode est transmis, les autres restent nuls.
 *
 * Le back rejetait silencieusement les payloads de l'ancien BO qui envoyaient
 * `montantFixe` et `taux` simultanément.
 */
export const buildTarifAmounts = (
  modeCalcul: ModeCalculTarification,
  amount: number | null,
  expression: string | null
): Pick<TarifDto, 'montantFixe' | 'montantFixeHT' | 'taux' | 'expression'> => ({
  montantFixe: isMontantTTC(modeCalcul) ? amount : null,
  montantFixeHT: isMontantHTMode(modeCalcul) ? amount : null,
  taux: isTauxMode(modeCalcul) ? amount : null,
  expression: isExpressionMode(modeCalcul) ? expression : null,
});

const isMontantTTC = (mode: ModeCalculTarification) =>
  mode === ModeCalculTarification.MontantFixe;
